import { createContext, useEffect, useState } from "react";
import { SessionContextValue, SessionProviderProps, SessionFilters, SessionSorter } from "./types";
import { useLocation } from "react-router-dom";
import { MergedColumnType } from "../../components/ui/table/utils/types";

/**
 * SessionProvider забезпечує збереження стану таблиці (tableState)
 * та сесійних даних (sessionData) у sessionStorage для окремої вкладки або розділу.
 *
 * 🟡 УВАГА:
 * Щоб `SessionContext` працював коректно:
 * 🔹 Компоненти, які використовують `tableState` або `sessionData`,
 *     мають бути **обгорнуті в `SessionProvider`**.
 * 🔹 Це означає, що ваш компонент повинен бути **всередині дерева JSX** провайдера:
 *
 * ✅ Правильно:
 * ```tsx
 * <SessionProvider url="/my-route">
 *   <MyTable />
 * </SessionProvider>
 * ```
 *
 * ❌ Неправильно:
 * ```tsx
 * <MyTable /> // Поза провайдером, context не працюватиме
 * ```
 *
 * 🔄 Дані автоматично зберігаються в sessionStorage за ключами,
 * що залежать від поточного маршруту (`route`).
 */

export const SessionContext = createContext<SessionContextValue>({
  tableState: { selectedRow: undefined, pageSize: 15, currentPage: 1, filters: undefined, sorter: undefined },
  setTableState: () => {},
  currentPageChange: () => {},
  tableStateAccess: { selectedRow: undefined, pageSize: 15, currentPage: 1 },
  setTableStateAccess: () => {},
  sessionData: {},
  setSessionData: () => {},
});

export function SessionProvider({ children, url }: SessionProviderProps) {
  const location = useLocation();
  const route = url || location.pathname;
  const [tableState, setTableState] = useState(
    JSON.parse(sessionStorage.getItem(`table_state_${route}`) ?? "{}") || {
      selectedRow: undefined,
      pageSize: 15,
      currentPage: 1,
    }
  );
  const [tableStateAccess, setTableStateAccess] = useState(
    JSON.parse(
      sessionStorage.getItem(`table_state_${route}_access`) ?? "{}"
    ) || {
      selectedRow: undefined,
      pageSize: 15,
      currentPage: 1,
    }
  );
  const [sessionData, setSessionData] = useState(
    JSON.parse(sessionStorage.getItem(`session_data_${route}`) ?? "{}") || {}
  );

  useEffect(() => {
    // console.log("TABLE STATE SessionContext useEffect ", JSON.stringify(tableState));
    sessionStorage.setItem(`table_state_${route}`, JSON.stringify(tableState));
  }, [tableState, route]);

  useEffect(() => {
    // console.log("SESSION DATA SessionContext useEffect");
    sessionStorage.setItem(
      `session_data_${route}`,
      JSON.stringify(sessionData)
    );
  }, [sessionData, route]);


  /*  Використовується для зміни сторінки таблиці, якщо вибраний рядок змінився.
      Це дозволяє автоматично переходити на сторінку, де знаходиться вибраний рядок,
      після зміни фільтрів або сортування.
      Викликається після зміни `tableState.selectedRow` або `tableData`.
      Використовується в компонентах таблиці для забезпечення зручності навігації.
      Параметри:
      - `tableData`: масив даних таблиці, який може бути відфільтрований та відсортований.
      Якщо `tableState.selectedRow` або `tableData` не визначені, функція нічого не робить.
      Якщо вибраний рядок не знайдено в відфільтрованих даних, функція також нічого не робить.
      Якщо вибраний рядок знайдено, функція розраховує нову сторінку на основі індексу цього рядка
      та встановлює її в `tableState.currentPage`.
  */
  const currentPageChange = (tableData: MergedColumnType[], selectedRow?: number | undefined) => {
    if (!(selectedRow ?? tableState?.selectedRow) || !tableData) return;
    const tableDataSF = applySortAndFilter(tableData, tableState.filters, tableState.sorter);
    const pageSize = tableState.pageSize || 15;
    const newIndex = tableDataSF.findIndex((item) => item.id === (selectedRow ?? tableState.selectedRow));
    newIndex > 0 && setSessionData(tableData[newIndex] || {});
    // newIndex = tableDataSF.findIndex((item) => item.id === tableState.selectedRow);
    if (newIndex !== -1) {
      setTableState({
        ...tableState,
        selectedRow: selectedRow ?? tableState.selectedRow,
        currentPage: Math.floor(newIndex / pageSize) + 1,
      });
    }
  };

  function applySortAndFilter(data: MergedColumnType[], filters: SessionFilters[], sorter: SessionSorter): MergedColumnType[] {
    let filtered = [...data];
    // фільтрація
    if (filters && filters.length > 0) {
      for (const filter of filters) {
        if (!filter.columnValue || filter.columnValue.length === 0) continue;
        filtered = filtered.filter((item) => {
          const itemValue = item[filter.columnKey as keyof MergedColumnType];
          return filter.columnValue!.some((val) => {
            if (val === null || val === undefined) return false;
            return String(itemValue).includes(String(val));
          });
        });
      }
    }
    // Сортування
    if (sorter && sorter.field && sorter.order && sorter.columnKey) {
      filtered.sort((a, b) => {
        const aVal = a[sorter.field as keyof MergedColumnType];
        const bVal = b[sorter.field as keyof MergedColumnType];
    
        if (aVal === bVal) return 0;
        const orderMultiplier = sorter.order === "ascend" ? 1 : -1;
    
        if (!isNaN(Number(aVal)) && !isNaN(Number(bVal))) {
          return (Number(aVal) - Number(bVal)) * orderMultiplier;
        }
    
        return String(aVal).localeCompare(String(bVal)) * orderMultiplier;
      });
    }
    return filtered;
  }
  
  return (
    <SessionContext.Provider
      value={{
        tableState,
        setTableState,
        currentPageChange,
        tableStateAccess,
        setTableStateAccess,
        sessionData,
        setSessionData,
      }}
    >
      {children}
    </SessionContext.Provider>
  );
}
